import React from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionKindPill } from './TransactionKindPill';

type PingPayoutTransaction = {
  amountOre: number;
  currency: string;
  direction: 'incoming' | 'outgoing' | 'neutral';
  occurredAt: string;
  metadata: {
    contractNumber: number;
    milestoneNumber: number;
    grossAmountOre: number;
    feeOre?: number;
    companyAmountOre?: number;
  };
};

export function PingPayoutHistoryItem({ transaction }: { transaction: PingPayoutTransaction }) {
  const { t, i18n } = useTranslation();
  const money = (ore: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: transaction.currency,
  }).format(ore / 100);
  const { metadata } = transaction;
  const reference = t('pingPayout.history.reference', metadata);
  const showBreakdown = typeof metadata.feeOre === 'number' && typeof metadata.companyAmountOre === 'number';
  const prefix = transaction.amountOre === 0 ? '' : transaction.direction === 'incoming' ? '+' : '−';
  return (
    <article className="border rounded overflow-hidden" aria-label={reference} data-testid="ping-payout-history-item">
      <div className="p-3 bg-light border-bottom">
        <div className="d-flex flex-wrap gap-2 align-items-center mb-1">
          <i className="bi bi-check-circle text-success" aria-hidden="true" />
          <TransactionKindPill kind="payout" />
          <h3 className="h6 mb-0">{t('pingPayout.history.title')}</h3>
          <span className="badge bg-success">{t('pingPayout.history.confirmed')}</span>
        </div>
        <p className="small text-muted mb-2">{reference}</p>
        <div className="fs-5 fw-bold">{prefix}{money(transaction.amountOre)}</div>
        <small className="text-muted">
          {t('pingPayout.history.confirmedAt', {
            date: new Intl.DateTimeFormat(i18n.language, {
              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            }).format(new Date(transaction.occurredAt)),
          })}
        </small>
      </div>
      {showBreakdown && (
        <dl className="p-3 mb-0 small">
          {[
            ['pingPayout.history.grossAmount', metadata.grossAmountOre],
            ['pingPayout.fee', metadata.feeOre!],
            ['pingPayout.companyAmount', metadata.companyAmountOre!],
          ].map(([label, amount]) => (
            <div className="d-flex flex-wrap justify-content-between gap-2" key={label}>
              <dt className="fw-normal">{t(String(label))}</dt>
              <dd className="text-end mb-1">{money(Number(amount))}</dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
}
